import api from '../api';
import { customerPortalApi } from '../apiInstances';
import type { CustomerPromotion } from '../../components/Promotions/banners/BannerProps';

/**
 * Receipts (payment and refund), for both the customer portal and the staff
 * payment-history screen.
 *
 * `api` here is the shared instance whose baseURL already ends in `/admin`
 * (see `services/api.ts`), so admin paths below are relative to
 * `/api/admin` — NOT re-derived from `apiInstances`, which only exports the
 * non-admin instances (`apiRoot`, `customerPortalApi`, `publicApi`).
 */

export interface CustomerReceipt {
  id: number;
  receipt_number: string;
  amount_cents: number;
  kind: 'payment' | 'refund';
  created_at: string;
  payment_method: string;
  entity_type: string;
  entity_id: number;
}

/** The signed-in customer's own receipts — deposits included. */
/**
 * The shop's promotion for the portal, or null when there is none. Served by the
 * customer instance because it is scoped to the signed-in customer's own shop —
 * the server takes the shop from their record, never from anything sent here.
 */
export async function fetchPortalPromotion(): Promise<CustomerPromotion | null> {
  const { data } = await customerPortalApi.get<CustomerPromotion | null>('/customer-portal/promotion');
  return data ?? null;
}

export async function fetchMyReceipts(): Promise<CustomerReceipt[]> {
  const { data } = await customerPortalApi.get<CustomerReceipt[]>('/customer-portal/receipts');
  return data;
}

/** Staff list, scoped to one payment (for the payment-history Actions column). */
export async function fetchReceiptsForPayment(paymentId: number): Promise<CustomerReceipt[]> {
  const { data } = await api.get<CustomerReceipt[]>('/receipts', { params: { payment_id: paymentId } });
  return data;
}

/**
 * Re-runs delivery for one receipt. Never throws on a failed send — a
 * customer with no email on file, or a mail-provider outage, comes back as
 * `emailed: false` with the reason recorded on the receipt row, not as a
 * rejected promise. Callers must check `emailed` rather than assume success
 * from a resolved call.
 */
export async function resendReceipt(id: number): Promise<{ stored: boolean; emailed: boolean }> {
  const { data } = await api.post(`/receipts/${id}/resend`, {});
  return data;
}

/**
 * Fetches the receipt PDF as a blob and saves it, the same way the
 * operational-reports CSV export does. Receipt PDFs are not publicly
 * reachable (no `/uploads/...` or `/storage/...` mount) — `/receipts/:id/pdf`
 * is the only route that serves them, and it requires the admin JWT this
 * instance attaches. A plain `<a href>` to a storage path would not carry
 * that auth and must never be constructed.
 */
export async function downloadReceiptPdf(id: number, receiptNumber: string): Promise<void> {
  const response = await api.get(`/receipts/${id}/pdf`, { responseType: 'blob' });
  saveBlobAs(response.data as Blob, `${receiptNumber}.pdf`);
}

/**
 * The customer's own copy of `downloadReceiptPdf` above, routed through
 * `customerPortalApi` (customer JWT) at `/customer-portal/receipts/:id/pdf`
 * rather than the admin instance. The server 404s identically for someone
 * else's receipt id and for a receipt whose PDF was never written (e.g. its
 * render or storage step failed) — callers should treat any 404 here as
 * "not available", not as evidence of which case it is.
 */
export async function downloadMyReceiptPdf(id: number, receiptNumber: string): Promise<void> {
  const response = await customerPortalApi.get(`/customer-portal/receipts/${id}/pdf`, {
    responseType: 'blob',
  });
  saveBlobAs(response.data as Blob, `${receiptNumber}.pdf`);
}

function saveBlobAs(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}
